/**
 * Accessibility ARIA Labels Fix
 * 
 * Adds descriptive aria-labels to links and buttons that don't have discernible names.
 * This fixes accessibility issues for screen readers.
 */

(function() {
  'use strict';
  
  /**
   * Fix Shopify preview bar iframe
   */
  function fixPreviewBarIframe() {
    const iframe = document.querySelector('iframe#PBarNextFrame');
    if (iframe && !iframe.getAttribute('title')) {
      iframe.setAttribute('title', 'Shopify Preview Bar');
    }
  }
  
  /**
   * Fix product action buttons (quickview, add to cart, wishlist, compare)
   */
  function fixProductActionButtons() {
    // Quickview buttons
    document.querySelectorAll('a.t4s-pr-quickview[data-action-quickview]').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        const productTitle = link.closest('.t4s-product')?.querySelector('.t4s-product-title, .t4s-title-pr')?.textContent.trim();
        link.setAttribute('aria-label', productTitle ? `Quick view ${productTitle}` : 'Quick view product');
      }
    });
    
    // Add to cart buttons
    document.querySelectorAll('a.t4s-pr-addtocart[data-action-atc]').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        const productTitle = link.closest('.t4s-product')?.querySelector('.t4s-product-title, .t4s-title-pr')?.textContent.trim();
        link.setAttribute('aria-label', productTitle ? `Add ${productTitle} to cart` : 'Add to cart');
      }
    });
    
    // Wishlist buttons
    document.querySelectorAll('a.t4s-pr-wishlist[data-action-wishlist]').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        const productTitle = link.closest('.t4s-product')?.querySelector('.t4s-product-title, .t4s-title-pr')?.textContent.trim();
        link.setAttribute('aria-label', productTitle ? `Add ${productTitle} to wishlist` : 'Add to wishlist');
      }
    });
    
    // Compare buttons
    document.querySelectorAll('a.t4s-pr-compare[data-action-compare]').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        const productTitle = link.closest('.t4s-product')?.querySelector('.t4s-product-title, .t4s-title-pr')?.textContent.trim();
        link.setAttribute('aria-label', productTitle ? `Add ${productTitle} to compare` : 'Add to compare');
      }
    });
  }
  
  /**
   * Fix product full-width overlay links
   */
  function fixProductFullWidthLinks() {
    document.querySelectorAll('a.t4s-full-width-link[data-pr-href]').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        const productTitle = link.closest('.t4s-product')?.querySelector('.t4s-product-title, .t4s-title-pr')?.textContent.trim();
        link.setAttribute('aria-label', productTitle ? `View ${productTitle}` : 'View product');
      }
    });
  }
  
  /**
   * Fix collection image links
   */
  function fixCollectionLinks() {
    document.querySelectorAll('a.t4s_cat_item_link').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        // Try to find collection title near the link
        const collectionItem = link.closest('.t4s-cat-item, .t4s-coll-item, .t4s-collection-item');
        const collectionTitle = collectionItem?.querySelector('.t4s-cat-title, .t4s-coll-title, .t4s-collection-title')?.textContent.trim();
        
        if (collectionTitle) {
          link.setAttribute('aria-label', `View ${collectionTitle} collection`);
        } else {
          // Fallback: try to get from href
          const href = link.getAttribute('href');
          if (href && href.includes('/collections/')) {
            const collectionSlug = href.split('/collections/')[1]?.split('/')[0] || '';
            const collectionName = collectionSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            link.setAttribute('aria-label', `View ${collectionName} collection`);
          } else {
            link.setAttribute('aria-label', 'View collection');
          }
        }
      }
    });
  }
  
  /**
   * Fix banner/image links
   */
  function fixBannerLinks() {
    // Banner links in banner sections
    document.querySelectorAll('.t4s-banner-item a.t4s-d-block, .t4s-banner-inner > a').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        const banner = link.closest('.t4s-banner-item, .t4s-banner-inner');
        const bannerTitle = banner?.querySelector('.t4s-banner-title, .t4s-heading-banner')?.textContent.trim();
        const bannerSubtitle = banner?.querySelector('.t4s-banner-subtitle, .t4s-sub-banner')?.textContent.trim();
        
        if (bannerTitle) {
          link.setAttribute('aria-label', bannerTitle + (bannerSubtitle ? ` - ${bannerSubtitle}` : ''));
        } else {
          link.setAttribute('aria-label', 'View promotion');
        }
      }
    });
  }
  
  /**
   * Fix social media / Instagram links
   */
  function fixSocialMediaLinks() {
    // Instagram carousel links
    document.querySelectorAll('a[href*="instagram.com"]').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        link.setAttribute('aria-label', 'View on Instagram');
      }
    });
    
    // Generic social links
    document.querySelectorAll('a.t4s-eff').forEach(function(link) {
      if (!link.getAttribute('aria-label') && !link.textContent.trim()) {
        const href = link.getAttribute('href') || '';
        if (href.includes('facebook.com')) {
          link.setAttribute('aria-label', 'Visit us on Facebook');
        } else if (href.includes('twitter.com')) {
          link.setAttribute('aria-label', 'Visit us on Twitter');
        } else if (href.includes('instagram.com')) {
          link.setAttribute('aria-label', 'Visit us on Instagram');
        } else if (href.includes('pinterest.com')) {
          link.setAttribute('aria-label', 'Visit us on Pinterest');
        } else if (href.includes('youtube.com')) {
          link.setAttribute('aria-label', 'Visit us on YouTube');
        }
      }
    });
  }
  
  /**
   * Apply all fixes
   */
  function applyAllFixes() {
    fixPreviewBarIframe();
    fixProductActionButtons();
    fixProductFullWidthLinks();
    fixCollectionLinks();
    fixBannerLinks();
    fixSocialMediaLinks();
  }
  
  /**
   * Initialize when DOM is ready
   */
  function init() {
    applyAllFixes();
    
    // Watch for dynamically added content (AJAX loading, etc.)
    if (window.MutationObserver) {
      const observer = new MutationObserver(function(mutations) {
        let shouldReapply = false;
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length) {
            shouldReapply = true;
          }
        });
        if (shouldReapply) {
          applyAllFixes();
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
    
    // Also reapply when Shopify section is rerendered (theme editor)
    document.addEventListener('shopify:section:load', applyAllFixes);
    document.addEventListener('shopify:section:reorder', applyAllFixes);
  }
  
  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Also run after short delays to catch late-loading content
  setTimeout(applyAllFixes, 100);
  setTimeout(applyAllFixes, 500);
  setTimeout(applyAllFixes, 1000);
  
})();
